import React from "react";
import {
  Alert,
  Box,
  IconButton,
  TextField,
  Typography,
} from "@mui/material";
import { ContentCopy, AccountBalance as UPIIcon } from "@mui/icons-material";
import {
  BANK_DETAILS,
  MERCHANT_UPI_ID,
  QR_CODE_IMAGE_PATH,
} from "../config/paymentConfig";
import { brandPalette as primaryBlue } from "../config/brandTheme";
import MerchantLegalNotice from "./MerchantLegalNotice";

const ManualUpiPaymentBlock = ({
  amountLabel,
  orderReference,
  transactionId,
  onTransactionIdChange,
  transactionIdError,
}) => {
  const copy = (text) => {
    navigator.clipboard?.writeText(text);
  };

  return (
    <Box>
      <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
        <UPIIcon sx={{ color: "success.main", mr: 1 }} />
        <Typography variant="h6" sx={{ fontWeight: 600, color: primaryBlue[700] }}>
          Pay via UPI (manual verification)
        </Typography>
      </Box>

      <Alert severity="info" sx={{ mb: 2 }}>
        Scan the QR or pay to <strong>{MERCHANT_UPI_ID}</strong>
        {amountLabel ? (
          <>
            {" "}
            for <strong>{amountLabel}</strong>
          </>
        ) : null}
        . Then enter your <strong>UPI transaction ID / UTR</strong> below. An admin
        will verify and confirm your order (usually within a few hours).
      </Alert>

      <Box sx={{ textAlign: "center", mb: 2 }}>
        <Box
          component="img"
          src={QR_CODE_IMAGE_PATH}
          alt="UPI QR code"
          sx={{
            maxWidth: 220,
            width: "100%",
            borderRadius: 2,
            border: `1px solid ${primaryBlue[200]}`,
          }}
        />
      </Box>

      <Box
        sx={{
          bgcolor: "white",
          p: 2,
          borderRadius: 1,
          mb: 2,
          border: "1px solid rgba(76, 175, 80, 0.2)",
        }}
      >
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <Box>
            <Typography variant="body2" color="text.secondary">UPI ID</Typography>
            <Typography variant="h6" sx={{ fontFamily: "monospace", fontWeight: 600 }}>
              {MERCHANT_UPI_ID}
            </Typography>
          </Box>
          <IconButton onClick={() => copy(MERCHANT_UPI_ID)} color="primary" size="small">
            <ContentCopy />
          </IconButton>
        </Box>
      </Box>

      <Box
        sx={{
          bgcolor: "rgba(0, 114, 206, 0.05)",
          p: 2,
          borderRadius: 1,
          border: `1px solid ${primaryBlue[200]}`,
          mb: 2,
        }}
      >
        <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
          Bank transfer (optional)
        </Typography>
        <Typography variant="body2">Account: {BANK_DETAILS.accountName}</Typography>
        <Typography variant="body2">A/C No: {BANK_DETAILS.accountNumber}</Typography>
        <Typography variant="body2">IFSC: {BANK_DETAILS.ifsc}</Typography>
        <Typography variant="body2">Branch: {BANK_DETAILS.branch}</Typography>
      </Box>

      {orderReference && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Payment reference: <strong>{orderReference}</strong>
        </Typography>
      )}

      <TextField
        fullWidth
        required
        label="UPI Transaction ID / UTR"
        placeholder="e.g. 123456789012"
        value={transactionId}
        onChange={(e) => onTransactionIdChange(e.target.value)}
        error={Boolean(transactionIdError)}
        helperText={
          transactionIdError ||
          "From your UPI app after successful payment (12+ characters recommended)"
        }
        sx={{ mb: 1 }}
      />

      <Box
        sx={{
          mt: 2,
          p: 2,
          borderRadius: 1,
          bgcolor: "rgba(0, 26, 77, 0.04)",
          border: `1px solid ${primaryBlue[200]}`,
        }}
      >
        <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1.5 }}>
          Contact information
        </Typography>
        <MerchantLegalNotice variant="body2" />
      </Box>
    </Box>
  );
};

export default ManualUpiPaymentBlock;
